import type { Breadcrumb, ErrorEvent } from '@sentry/vue';
import type { TransactionEvent } from '@sentry/types';

/**
 * Filtri e sanificazione applicati agli eventi prima che lascino il browser.
 *
 * L'app vive dentro una pagina WordPress, quindi gli handler globali di Sentry vedono anche
 * errori di script che non sono nostri; e le API trattano dati personali (CF, IBAN, email,
 * OTP): niente di tutto questo deve finire nel payload né consumare quota.
 */

/** Oltre questa soglia, in una stessa pagina caricata, non inviamo più eventi. */
const MAX_EVENTS_PER_SESSION = 20;

/** Tronca le stringhe libere (descrizioni d'errore del server) per non portarsi dietro body interi. */
const MAX_TEXT_LENGTH = 200;

let sentCount = 0;
const seenKeys = new Set<string>();

/** Toglie query string e hash: possono contenere codici documento, id di sessione, token. */
export function stripQuery<T extends string | null | undefined>(url: T): T {
	if (typeof url !== 'string') {
		return url;
	}
	return url.split(/[?#]/)[0] as T;
}

/**
 * Riduce un endpoint API a una forma stabile per raggruppare gli errori:
 * niente query string, e id numerici / UUID / token lunghi sostituiti da `:id`.
 */
export function normalizeEndpoint(url?: string | null): string | null {
	const path = stripQuery(url);
	if (!path) {
		return null;
	}
	return path
		.split('/')
		.map((segment) => {
			if (/^\d+$/.test(segment)) return ':id';
			if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return ':id';
			if (segment.length >= 16 && /\d/.test(segment)) return ':id';
			return segment;
		})
		.join('/');
}

export function truncate(value: unknown, max = MAX_TEXT_LENGTH): string | null {
	if (typeof value !== 'string') {
		return null;
	}
	return value.length > max ? `${value.slice(0, max)}…` : value;
}

/** Chiave con cui deduplichiamo gli eventi: la fingerprint esplicita, altrimenti tipo+messaggio. */
function eventKey(event: ErrorEvent): string {
	const custom = event.fingerprint?.filter((f) => f !== '{{ default }}');
	if (custom?.length) {
		return custom.join('|');
	}
	const exception = event.exception?.values?.[0];
	if (exception) {
		return `${exception.type ?? ''}|${exception.value ?? ''}`;
	}
	return event.message ?? '';
}

/**
 * Limita il volume per pagina caricata: un errore in loop da un solo utente non deve poter
 * esaurire la quota, e la stessa issue non va inviata più volte nella stessa sessione.
 */
export function passesSessionLimit(event: ErrorEvent): boolean {
	if (sentCount >= MAX_EVENTS_PER_SESSION) {
		return false;
	}
	const key = eventKey(event);
	if (seenKeys.has(key)) {
		return false;
	}
	seenKeys.add(key);
	sentCount++;
	return true;
}

/** Rimuove dall'evento ciò che non serve a diagnosticare e che è dato personale. */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
	if (event.user) {
		delete event.user.email;
		delete event.user.username;
		delete event.user.ip_address;
	}

	if (event.request) {
		event.request.url = stripQuery(event.request.url);
		delete event.request.query_string;
		delete event.request.cookies;
		delete event.request.data;
		if (event.request.headers) {
			for (const key of Object.keys(event.request.headers)) {
				if (key.toLowerCase() === 'referer') {
					event.request.headers[key] = stripQuery(event.request.headers[key]);
				}
			}
		}
	}

	if (event.tags?.['api.endpoint']) {
		event.tags['api.endpoint'] = normalizeEndpoint(String(event.tags['api.endpoint'])) ?? '';
	}

	const apiRequest = event.contexts?.api_request as Record<string, unknown> | undefined;
	if (apiRequest) {
		delete apiRequest.response_body;
	}

	return event;
}

/** Filtra e sanifica i breadcrumb: niente console (porta con sé body di risposta), niente query string. */
export function filterBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
	if (breadcrumb.category === 'console' || breadcrumb.category === 'body') {
		return null;
	}

	const data = breadcrumb.data;
	if (data) {
		if (typeof data.url === 'string') {
			data.url = stripQuery(data.url);
		}
		if (typeof data.from === 'string') {
			data.from = stripQuery(data.from);
		}
		if (typeof data.to === 'string') {
			data.to = stripQuery(data.to);
		}
	}

	return breadcrumb;
}

/** Span che non servono a diagnosticare e che da soli sono la maggior parte della quota. */
const DROPPED_SPAN_OP_PREFIXES = ['resource.', 'ui.long-task', 'ui.long-animation-frame', 'ui.interaction'];

/** Toglie gli span di risorse statiche dalle transazioni e la query string dal nome. */
export function filterTransactionSpans(event: TransactionEvent): TransactionEvent {
	if (event.spans) {
		event.spans = event.spans.filter(
			(span) => !DROPPED_SPAN_OP_PREFIXES.some((prefix) => span.op?.startsWith(prefix))
		);
	}
	if (event.transaction) {
		event.transaction = stripQuery(event.transaction);
	}
	if (event.request) {
		event.request.url = stripQuery(event.request.url);
		delete event.request.query_string;
		delete event.request.cookies;
	}
	return event;
}
